import { useState } from "react";
import { message } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  CONNECTION_PLATFORMS,
  MANUAL_SPEND_PLATFORMS,
  clientsService,
  type ClientDetail,
  type PacingSplit,
} from "@/services/clientsService";

/**
 * S3 of the cutover plan: the two webapp surfaces that had no Core home —
 * platform-connection linking and hand-entered spend. Both write
 * Planner-first on the backend (mirrored tables would revert local-only
 * edits); a 503 toast means authoring is flag-dark on this environment.
 */

const detail = (err: unknown) =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

const httpStatus = (err: unknown) =>
  (err as { response?: { status?: number } })?.response?.status;

const usd = (v: number | null | undefined) =>
  v == null ? "—" : `$${Math.round(v).toLocaleString("en-US")}`;

const shortDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 10)
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/** Platforms whose proposed allotment differs from current by $1 or more. */
const splitChanges = (split: PacingSplit) =>
  Object.keys(split.current)
    .filter((p) => Math.abs((split.proposed[p] ?? 0) - split.current[p]) >= 1)
    .map((p) => ({ platform: p, from: split.current[p], to: split.proposed[p] ?? 0 }));

/**
 * Channel split: "Let Core decide the split" (run rate). GET is a read-only
 * preview; the toggle and "Apply now" are staff writes (Planner first, ended
 * cycles frozen). A 409 on GET is the no-funded-platforms empty state.
 *
 * Named CHANNEL, not platform: the underlying DB column is `platform` but the
 * rows are actually ad-channel budgets (Google Ads and Google LSA both sit
 * under Google; Facebook + Instagram sit under Meta). Card title reflects
 * how the team thinks about it.
 */
export function ChannelSplitCard({
  contractId,
  onApplied,
}: {
  contractId: string;
  onApplied: () => void;
}) {
  const qc = useQueryClient();
  const queryKey = ["clients", contractId, "pacing-split"];

  const query = useQuery({
    queryKey,
    queryFn: () => clientsService.pacingSplit(contractId),
    retry: (count, err) => httpStatus(err) !== 409 && count < 2,
  });

  const toggle = useMutation({
    mutationFn: (on: boolean) => clientsService.setPacingSplit(contractId, on),
    onSuccess: (setting) => {
      message.success(setting.auto_split ? "Core will decide the split" : "Split left to staff");
      qc.invalidateQueries({ queryKey });
    },
    onError: (err) => message.error(detail(err) ?? "Save failed"),
  });

  const apply = useMutation({
    mutationFn: () => clientsService.applyPacingSplit(contractId),
    onSuccess: (res) => {
      if (res.skipped) message.info(res.skipped);
      else message.success("Split applied");
      qc.invalidateQueries({ queryKey });
      onApplied();
    },
    onError: (err) => message.error(detail(err) ?? "Apply failed"),
  });

  const split = query.data;
  const changes = split ? splitChanges(split) : [];
  const noFundedPlatforms = query.isError && httpStatus(query.error) === 409;

  const confirmApply = () => {
    if (!split || changes.length === 0) return;
    const summary = changes
      .map((c) => `${c.platform} ${usd(c.from)} → ${usd(c.to)}`)
      .join(", ");
    if (window.confirm(`Apply this split now?\n\n${summary}\n\nTotal stays ${usd(split.total)}.`)) {
      apply.mutate();
    }
  };

  return (
    <Card className="border-0 p-4 shadow-sm">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold">Channel split</h2>
        <Label className="flex items-center gap-2 text-xs text-muted-foreground">
          Let Core decide the split (run rate)
          <Switch
            checked={split?.setting.auto_split ?? false}
            disabled={!split || toggle.isPending}
            onCheckedChange={(on) => toggle.mutate(on)}
            aria-label="Let Core decide the split (run rate)"
          />
        </Label>
      </div>
      {split?.setting.set_by ? (
        <p className="mb-2 text-right text-[11px] text-muted-foreground">
          Set by {split.setting.set_by}
          {split.setting.set_at ? ` · ${shortDate(split.setting.set_at)}` : ""}
        </p>
      ) : null}

      {query.isPending ? (
        <div className="space-y-2">
          <div className="h-8 animate-pulse rounded bg-muted" />
          <div className="h-8 animate-pulse rounded bg-muted" />
          <div className="h-8 animate-pulse rounded bg-muted" />
        </div>
      ) : noFundedPlatforms ? (
        <p className="py-4 text-sm text-muted-foreground">No funded platforms to split.</p>
      ) : query.isError || !split ? (
        <p className="py-4 text-sm text-destructive">{detail(query.error) ?? "Couldn't load the split."}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/20">
                  <TableHead>Channel</TableHead>
                  <TableHead className="text-right">Current</TableHead>
                  <TableHead className="text-right whitespace-nowrap">
                    Trailing avg (last {split.basis_cycles} cycles)
                  </TableHead>
                  <TableHead className="text-right">Suggested</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.keys(split.current).map((p) => {
                  const moved = Math.abs((split.proposed[p] ?? 0) - split.current[p]) >= 1;
                  return (
                    <TableRow key={p}>
                      <TableCell className="whitespace-nowrap">
                        {p}
                        {p === split.sink ? (
                          <Badge variant="outline" className="ml-2 font-normal text-muted-foreground">
                            sink
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{usd(split.current[p])}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {usd(split.trailing_avg_spend[p])}
                      </TableCell>
                      <TableCell className={cn("text-right tabular-nums", moved && "font-semibold text-coral-dark")}>
                        {usd(split.proposed[p])}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              <span className="tabular-nums">Total {usd(split.total)}</span>
              {split.capped ? (
                <span className="ml-2">· Stepped: no channel moves more than a set share at once</span>
              ) : null}
            </div>
            <Button
              size="sm"
              className="h-8"
              disabled={apply.isPending || changes.length === 0}
              onClick={confirmApply}
            >
              {apply.isPending ? "Applying…" : changes.length === 0 ? "Already at split" : "Apply now"}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

export function ConnectionsCard({
  client,
  onSaved,
}: {
  client: ClientDetail;
  onSaved: () => void;
}) {
  const initial: Record<string, string> = {
    google_ads: client.ads_customer_id ?? "",
    google_lsa: client.lsa_customer_id ?? "",
    meta_ads: "",
    bing: "",
  };
  const [ids, setIds] = useState(initial);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const save = async (key: string) => {
    setSavingKey(key);
    try {
      await clientsService.setConnection(
        client.contract_id as string,
        key,
        ids[key].trim() || null,
      );
      message.success(`${key} ${ids[key].trim() ? "linked" : "unlinked"} — Planner first, mirror follows`);
      onSaved();
    } catch (err) {
      message.error(detail(err) ?? "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Card className="border-0 p-4 shadow-sm">
      <h2 className="mb-1 text-sm font-bold">Platform connections</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        Customer ids the pacing and push engines resolve. Dashes allowed; clearing a field unlinks.
      </p>
      <div className="space-y-2">
        {CONNECTION_PLATFORMS.map(({ key, label }) => (
          <div key={key} className="flex items-center gap-2">
            <Label className="w-28 shrink-0 text-xs text-muted-foreground">{label}</Label>
            <Input
              value={ids[key]}
              placeholder={key === "meta_ads" || key === "bing" ? "not linked" : ""}
              onChange={(e) => setIds((prev) => ({ ...prev, [key]: e.target.value }))}
              className="h-8 font-mono text-xs"
            />
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={savingKey === key}
              onClick={() => save(key)}
            >
              {savingKey === key ? "…" : "Save"}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ManualSpendCard({
  client,
  onSaved,
}: {
  client: ClientDetail;
  onSaved: () => void;
}) {
  const [platform, setPlatform] = useState<string>("Meta Ads");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [spend, setSpend] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await clientsService.recordSpend(client.contract_id as string, {
        platform,
        billing_period_start: start,
        billing_period_end: end || undefined,
        spend_dollars: Number(spend),
      });
      message.success(`${platform} spend recorded — Planner first, mirror follows`);
      setSpend("");
      onSaved();
    } catch (err) {
      message.error(detail(err) ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-0 p-4 shadow-sm">
      <h2 className="mb-1 text-sm font-bold">Record manual spend</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        Hand-entered platforms only — Google Ads and LSA spend is pulled automatically.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MANUAL_SPEND_PLATFORMS.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          type="date"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          className="h-8 text-xs"
          aria-label="Billing period start"
        />
        <Input
          type="date"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          className="h-8 text-xs"
          aria-label="Billing period end (optional)"
        />
        <Input
          type="number"
          min={0}
          placeholder="Spend $"
          value={spend}
          onChange={(e) => setSpend(e.target.value)}
          className="h-8 text-xs"
        />
        <Button
          size="sm"
          className="h-8"
          disabled={saving || !start || spend.trim() === ""}
          onClick={save}
        >
          {saving ? "Saving…" : "Record"}
        </Button>
      </div>
    </Card>
  );
}
